import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, ArrowUpRight, Loader2 } from "lucide-react";
import { RevealLines } from "@/components/site/RevealText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "nexoriq_admin_token";

export default function AdminLogin() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Already logged in?
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    axios
      .get(`${API}/admin/me`, { headers: { "X-Admin-Token": t } })
      .then(() => nav("/admin/dashboard", { replace: true }))
      .catch(() => localStorage.removeItem(TOKEN_KEY));
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Signed in.");
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="min-h-[80vh] flex items-center">
      <section className="px-6 md:px-10 py-16 md:py-24 grid grid-cols-12 gap-6 md:gap-10 items-start w-full">
        <div className="col-span-12 md:col-span-6">
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2 mb-6">
            → Admin · Restricted
          </p>
          <RevealLines
            as="h1"
            lines={[
              <span key="1">Nexoriq</span>,
              <span key="2" className="font-serif italic font-normal text-nx-navy2">
                sourcing desk.
              </span>,
            ]}
            className="font-cabinet font-black tracking-[-0.045em] text-nx-ink text-[13vw] md:text-[7.5vw] leading-[0.9]"
            stagger={0.14}
            mode="mount"
          />
          <p className="mt-8 max-w-md text-nx-navy2 leading-relaxed">
            Restricted view. Enter the admin password to review submitted sourcing briefs, response status, and category traffic.
          </p>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="col-span-12 md:col-span-6 md:col-start-7 border border-nx-navy bg-white p-8 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
              <Lock size={12} /> Restricted access
            </div>
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-steel2">
              NX / ADMIN
            </span>
          </div>

          <label className="block">
            <span className="flex items-center mb-2 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-nx-steel2">
              <span className="mr-2">[01]</span>Password<span className="text-nx-navy">*</span>
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              data-testid="admin-password"
              className="w-full bg-transparent border-b border-nx-navy/60 focus:border-nx-navy py-3 outline-none font-ibm text-nx-navy"
              placeholder="••••••••••••"
            />
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="nx-btn mt-8 w-full justify-center disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Signing in
              </>
            ) : (
              <>
                Enter dashboard <ArrowUpRight size={14} />
              </>
            )}
          </button>

          <p className="mt-6 text-xs text-nx-steel2 leading-relaxed">
            Session expires after 12 hours. Contact MJ Gill for access issues.
          </p>
        </motion.form>
      </section>
    </div>
  );
}
